/**
 * @file carousel-scroll.tsx
 * Client scroll engine handling gesture tracking, scroll physics,
 * keyline matrix calculations, dynamic masks, and keyboard navigation.
 */

"use client";

import * as React from "react";
import { cn } from "../../lib/utils";
import type { CarouselProps } from "./carousel.types";
import { CarouselItem } from "./carousel-item";
import { useCarouselA11y } from "./hooks/useCarouselA11y";
import {
	calculateMaskClipForOffset,
	calculateParallaxOffset,
	useCarouselKeylines,
} from "./hooks/useCarouselKeylines";

export interface CarouselScrollClientProps<T = unknown>
	extends CarouselProps<T> {
	totalItemsCount: number;
}

export function CarouselScrollClient<T = unknown>({
	children,
	items,
	renderItem,
	layout = "multi-browse",
	preferredItemWidth = 300,
	itemSpacing = 8,
	contentPadding = 16,
	verticalPadding = 8,
	userScrollEnabled = true,
	totalItemsCount,
	className,
	style,
	...props
}: CarouselScrollClientProps<T>) {
	const trackRef = React.useRef<HTMLDivElement>(null);
	const [containerWidth, setContainerWidth] = React.useState<number>(600);

	// Refs for rAF scroll tracking and drag gesture physics
	const rafIdRef = React.useRef<number | null>(null);
	const isDraggingRef = React.useRef<boolean>(false);
	const hasMovedRef = React.useRef<boolean>(false);
	const startPosRef = React.useRef<number>(0);
	const startScrollRef = React.useRef<number>(0);

	// Calculate stable keyline sizes without per-frame React re-renders
	const keylines = useCarouselKeylines({
		containerWidth,
		preferredItemWidth,
		layout,
		itemSpacing,
		contentPadding,
		totalItems: totalItemsCount,
	});

	// High performance rAF scroll handler updating CSS variables and GPU masks directly
	const handleScroll = React.useCallback(() => {
		if (rafIdRef.current !== null) return;

		rafIdRef.current = requestAnimationFrame(() => {
			const track = trackRef.current;
			if (track) {
				const scrollVal =
					layout === "full-screen" ? track.scrollTop : track.scrollLeft;
				track.style.setProperty("--carousel-scroll-left", `${scrollVal}px`);

				if (layout !== "full-screen") {
					const prefersReducedMotion =
						typeof window !== "undefined" &&
						window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

					let accumulatedShift = 0;

					const items = track.children;
					for (let i = 0; i < items.length; i++) {
						const itemEl = items[i] as HTMLElement;
						if (!itemEl || itemEl.getAttribute("role") !== "group") continue;

						if (prefersReducedMotion) {
							itemEl.style.clipPath = "none";
							itemEl.style.transform = "none";
							const innerContent = itemEl.firstElementChild as HTMLElement;
							if (innerContent) innerContent.style.transform = "none";
							continue;
						}

						// 1. Calculate native offset & visual (post-shift) offset
						const nativeOffset = itemEl.offsetLeft - scrollVal;
						const visualOffset = nativeOffset - accumulatedShift;

						// 2. Calculate dynamic mask based on true visual position
						const maskPercent = calculateMaskClipForOffset(
							visualOffset,
							containerWidth,
							keylines.largeWidth,
							keylines.mediumWidth,
							keylines.smallWidth,
							contentPadding,
							layout,
						);

						// 3. Calculate how many pixels are visually removed from THIS item
						const clippedPixels = (maskPercent / 100) * keylines.largeWidth;

						// 4. SHIFT item left by the accumulated lost space of all PREVIOUS items
						itemEl.style.transform =
							accumulatedShift > 0
								? `translateX(${-accumulatedShift}px)`
								: "none";

						// 5. Apply the mask to visually shrink the item
						itemEl.style.clipPath =
							maskPercent > 0
								? `inset(0px ${maskPercent}% 0px 0px round 28px)`
								: "none";

						// 6. Apply parallax to inner content
						const innerContent = itemEl.firstElementChild as HTMLElement;
						if (innerContent) {
							const parallaxX = calculateParallaxOffset(
								visualOffset,
								maskPercent,
								keylines.largeWidth,
							);
							innerContent.style.transform =
								parallaxX !== 0 ? `translateX(${parallaxX}px)` : "none";
						}

						// 7. Add this item's clipped space to the accumulator for the next items
						accumulatedShift += clippedPixels;
					}
				}
			}
			rafIdRef.current = null;
		});
	}, [
		layout,
		containerWidth,
		keylines.largeWidth,
		keylines.mediumWidth,
		keylines.smallWidth,
		contentPadding,
	]);

	// Measure track container width using ResizeObserver and apply initial keylines
	React.useEffect(() => {
		const track = trackRef.current;
		if (!track) return;

		const updateWidth = () => {
			if (track.clientWidth > 0) {
				setContainerWidth(track.clientWidth);
			}
		};

		updateWidth();
		const observer = new ResizeObserver(updateWidth);
		observer.observe(track);
		return () => observer.disconnect();
	}, []);

	// Trigger initial keyline mask pass on mount or dimension changes
	React.useEffect(() => {
		handleScroll();
	}, [handleScroll]);

	// Cleanup rAF on unmount
	React.useEffect(() => {
		return () => {
			if (rafIdRef.current !== null) {
				cancelAnimationFrame(rafIdRef.current);
			}
		};
	}, []);

	// Drag Gesture Physics
	const handlePointerDown = React.useCallback(
		(e: React.PointerEvent<HTMLDivElement>) => {
			if (!userScrollEnabled || e.button !== 0) return;
			const track = trackRef.current;
			if (!track) return;

			isDraggingRef.current = true;
			hasMovedRef.current = false;
			startPosRef.current = layout === "full-screen" ? e.clientY : e.clientX;
			startScrollRef.current =
				layout === "full-screen" ? track.scrollTop : track.scrollLeft;

			try {
				(e.target as HTMLElement).setPointerCapture?.(e.pointerId);
			} catch {
				// Ignore pointer capture errors if unsupported
			}
		},
		[userScrollEnabled, layout],
	);

	const handlePointerMove = React.useCallback(
		(e: React.PointerEvent<HTMLDivElement>) => {
			if (!isDraggingRef.current || !trackRef.current) return;

			const currentPos = layout === "full-screen" ? e.clientY : e.clientX;
			const delta = currentPos - startPosRef.current;

			if (Math.abs(delta) > 4) {
				hasMovedRef.current = true;
			}

			if (hasMovedRef.current) {
				if (layout === "full-screen") {
					trackRef.current.scrollTop = startScrollRef.current - delta;
				} else {
					trackRef.current.scrollLeft = startScrollRef.current - delta;
				}
			}
		},
		[layout],
	);

	const handlePointerUp = React.useCallback(
		(e: React.PointerEvent<HTMLDivElement>) => {
			if (!isDraggingRef.current) return;
			isDraggingRef.current = false;

			try {
				(e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
			} catch {
				// Ignore if already released
			}
		},
		[],
	);

	// Suppress click events if the user performed a drag gesture
	const handleClickCapture = React.useCallback((e: React.MouseEvent) => {
		if (hasMovedRef.current) {
			e.stopPropagation();
			e.preventDefault();
			hasMovedRef.current = false;
		}
	}, []);

	// Scroll into view helper for accessibility
	const scrollToIndex = React.useCallback(
		(index: number) => {
			const track = trackRef.current;
			if (!track) return;
			const itemEl = track.children[index] as HTMLElement | undefined;
			if (!itemEl) return;
			const targetPos = Math.max(0, itemEl.offsetLeft - contentPadding);
			track.scrollTo({
				left: targetPos,
				behavior: "smooth",
			});
		},
		[contentPadding],
	);

	// Accessibility keyboard navigation hook
	const { focusedIndex, handleKeyDown, getItemA11yProps } = useCarouselA11y({
		totalItems: totalItemsCount,
		scrollToIndex,
	});

	// Track layout classes
	const isFullScreen = layout === "full-screen";
	const isHero = layout === "hero" || layout === "center-aligned-hero";

	const trackClasses = cn(
		// Base layout & GPU containment
		"relative box-border select-none [contain:layout_style]",
		userScrollEnabled && "cursor-grab active:cursor-grabbing",
		// Scrollbar hide
		"[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
		// Full-screen: vertical snap column
		isFullScreen
			? "flex flex-col flex-1 min-h-0 overflow-x-hidden overflow-y-auto p-0 gap-0 [scroll-snap-type:y_mandatory] [touch-action:pan-y]"
			: "flex flex-row items-stretch overflow-x-auto overflow-y-hidden [-webkit-overflow-scrolling:touch] [touch-action:pan-x]",
		// Snap for hero/center-aligned-hero
		isHero && !isFullScreen && "[scroll-snap-type:x_mandatory]",
		// Disabled
		!userScrollEnabled &&
			"overflow-x-hidden pointer-events-none [touch-action:none]",
		className,
	);

	const trackStyle: React.CSSProperties = isFullScreen
		? { ...style }
		: {
				paddingLeft: `${contentPadding}px`,
				paddingRight:
					layout === "uncontained" || layout === "uncontained-multi-aspect"
						? 0
						: `${contentPadding}px`,
				scrollPaddingLeft: `${contentPadding}px`,
				scrollPaddingRight: `${contentPadding}px`,
				paddingTop: `${verticalPadding}px`,
				paddingBottom: `${verticalPadding}px`,
				gap: `${itemSpacing}px`,
				...style,
			};

	// Determine rendered slides (CSR array vs RSC children)
	const renderSlides = () => {
		if (items && renderItem) {
			return items.map((item, index) => {
				const itemWidth = keylines.largeWidth;
				const maskClip = keylines.getItemMaskClip(index);
				const parallax = keylines.getItemParallaxOffset(index);
				const a11yProps = getItemA11yProps(index);
				const itemKey =
					typeof item === "object" && item !== null && "id" in item
						? (item as { id: React.Key }).id
						: typeof item === "object" && item !== null && "key" in item
							? (item as { key: React.Key }).key
							: index;

				return (
					<CarouselItem
						key={itemKey}
						index={index}
						totalItems={totalItemsCount}
						layout={layout}
						width={itemWidth}
						minContentWidth={keylines.largeWidth}
						maskClipPercent={maskClip}
						parallaxOffset={parallax}
						isFocused={focusedIndex === index}
						{...a11yProps}
					>
						{renderItem(item, index)}
					</CarouselItem>
				);
			});
		}

		return React.Children.map(children, (child, index) => {
			if (!React.isValidElement(child)) return child;

			const itemWidth = keylines.largeWidth;
			const maskClip = keylines.getItemMaskClip(index);
			const parallax = keylines.getItemParallaxOffset(index);
			const a11yProps = getItemA11yProps(index);
			const childKey = child.key != null ? child.key : index;

			return (
				<CarouselItem
					key={childKey}
					index={index}
					totalItems={totalItemsCount}
					layout={layout}
					width={itemWidth}
					minContentWidth={keylines.largeWidth}
					maskClipPercent={maskClip}
					parallaxOffset={parallax}
					isFocused={focusedIndex === index}
					{...a11yProps}
				>
					{child}
				</CarouselItem>
			);
		});
	};

	return (
		<section
			ref={trackRef}
			aria-roledescription="carousel"
			aria-label={props["aria-label"] || "Content carousel"}
			// biome-ignore lint/a11y/noNoninteractiveTabindex: carousel region requires keyboard focus for arrow navigation
			tabIndex={0}
			className={trackClasses}
			style={trackStyle}
			onScroll={handleScroll}
			onPointerDown={handlePointerDown}
			onPointerMove={handlePointerMove}
			onPointerUp={handlePointerUp}
			onPointerCancel={handlePointerUp}
			onClickCapture={handleClickCapture}
			onKeyDown={handleKeyDown}
			{...props}
		>
			{renderSlides()}
			{!isFullScreen && (
				<div
					aria-hidden="true"
					className="shrink-0 pointer-events-none"
					style={{
						width: 0,
						marginRight:
							keylines.maxAccumulatedShift > 0
								? `-${keylines.maxAccumulatedShift}px`
								: undefined,
					}}
				/>
			)}
		</section>
	);
}
